import {
  Component,
  ComponentListener,
  EcsSystem as CoreEcsSystem,
  Entity
} from '@inter-ikea-core/core';
import { CoreApi } from '../CoreApi';
import { Product } from '../../types';

/**
 * @deprecated
 */
export abstract class System {
  coreApi: CoreApi;

  constructor(coreApi: CoreApi) {
    this.coreApi = coreApi;
  }

  abstract register(): void;
}

export class PlannerComponentListener<TComponent extends Component> implements ComponentListener {
  ownerSystem: PlannerEcsSystem<TComponent>;
  componentId: number;

  constructor(owner: PlannerEcsSystem<TComponent>, componentId: number) {
    this.ownerSystem = owner;
    this.componentId = componentId;
  }

  onAdd(entity: Entity, newComponent: TComponent): void {
    this.ownerSystem.onAdd(entity, newComponent);
  }
  onUpdate(entity: Entity, oldComponent: TComponent, newComponent: TComponent): void {
    this.ownerSystem.onUpdate(entity, oldComponent, newComponent);
  }
  onRemove(entity: Entity, oldComponent: TComponent): void {
    this.ownerSystem.onRemove(entity, oldComponent);
  }
}

export abstract class PlannerEcsSystem<TComponent extends Component> implements CoreEcsSystem {
  id: string;
  isEnabled: boolean;
  componentId: number | null;
  systemsBeforeMe: string[] | null = null;
  systemsAfterMe: string[] | null = null;
  doingChangesOnComponent: number[] = [];
  ignoreEntitiesWithComponents: number[] | null = null;
  coreApi: CoreApi;
  componentListeners: ComponentListener[];
  mandatoryComponents: number[] | null = null;

  constructor(coreApi: CoreApi, id: string, componentId: number | null) {
    this.id = id;
    this.coreApi = coreApi;
    this.isEnabled = true;
    this.componentId = componentId;
    this.componentListeners =
      componentId !== null ? [new PlannerComponentListener(this, componentId)] : [];
  }
  onRegister(): void {}
  onUnregister(): void {}

  /**
   * Called when component is added
   * @param entity with component
   * @param newComponent the new component
   */
  abstract onAdd(entity: Entity, newComponent: TComponent): void;

  /**
   * Called when component is updated
   * @param entity with component
   * @param oldComponent the old component
   * @param newComponent the new component
   */
  abstract onUpdate(entity: Entity, oldComponent: TComponent, newComponent: TComponent): void;

  /**
   * Called when component is removed
   * @param entity with component
   * @param oldComponent the old component
   */
  abstract onRemove(entity: Entity, oldComponent: TComponent): void;

  /**
   * Enable system
   */
  enable(): void {
    this.isEnabled = true;
  }

  /**
   * Disable system
   */
  disable(): void {
    this.isEnabled = false;
  }
}

export type OnAddFn<TComponent extends Component> = (
  entity: Entity,
  newComponent: TComponent
) => void;

export type OnUpdateFn<TComponent extends Component> = (
  entity: Entity,
  oldComponent: TComponent,
  newComponent: TComponent
) => void;

export type OnRemoveFn<TComponent extends Component> = (
  entity: Entity,
  oldComponent: TComponent
) => void;

/**
 * A system that implements this needs avialable products in market
 */
export interface WithMarketProducts {
  setMarketProducts(marketProducts: Product[]): void;
}

export const initSystemsWithMarketProducts = (
  productsInMarket: Product[],
  coreApi: CoreApi
): void => {
  const systemsWithMarketProducts = coreApi
    .getServices()
    .entityManager.getSystemsRegistered()
    .filter(system => 'setMarketProducts' in system);
  systemsWithMarketProducts.forEach((system: any) => system.setMarketProducts(productsInMarket));
};

/**
 * This class is used to register a system that listens to
 * multiple components. For each component it will register a subsystem
 * that triggers onAdd, onUpdate and onRemove for that specific component.
 *
 * Classes that extend this class needs to check which component triggered the
 * change in onAdd, onUpdate and onRemove functions.
 */
export abstract class PlannerEcsMultiSystem<TComponent extends Component> extends PlannerEcsSystem<
  TComponent
> {
  constructor(coreApi: CoreApi, id: string, componentIds: number[]) {
    super(coreApi, id, null);
    this.onAdd = this.onAdd.bind(this);
    this.onUpdate = this.onUpdate.bind(this);
    this.onRemove = this.onRemove.bind(this);

    this.componentListeners = componentIds.map(
      componentId => new PlannerComponentListener(this, componentId)
    );
  }
  /**
   *
   * @param entity the entity to which the component was added
   * @param newComponent the component that was added
   */
  abstract onAdd(entity: Entity, newComponent: TComponent): void;
  /**
   *
   * @param entity the entity on which the component was updated
   * @param oldComponent the component before the change
   * @param newComponent the component after the change
   */
  abstract onUpdate(entity: Entity, oldComponent: TComponent, newComponent: TComponent): void;

  /**
   *
   * @param entity the entity on which the component was removed
   * @param newComponent the component after it was removed
   */
  abstract onRemove(entity: Entity, oldComponent: TComponent): void;
}
